/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment } from 'react';
import { Box } from '@material-ui/core';
import { Field, FieldProps, Form, FormikProps } from 'formik';
import useTranslate from 'hooks/useTranslate';
import {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';
import Link from 'components/Link';
import Button from 'components/Button';
import IovationButton from 'features/IovationButton';

import PageContainer from 'components/PageContainer';
import { FormTextField } from 'components/TextField';

import { FormValues } from './FormPrescreenId';

import styles from './styles';
import useWorkflow, { Step } from 'hooks/useWorkflow';
import ActionButtonContainer from 'components/ActionButtonsContainer';

import { prescreenIdMask } from 'utils/masks';
import CancelButton from 'features/CancelButton';
import Title from 'components/Title';

interface PropTypes extends FormikProps<FormValues> {
  attempts: number;
  maxAttempts: number;
}

const FormPrescreenIdView: React.FC<PropTypes> = props => {
  const { isValid, isSubmitting, attempts, maxAttempts } = props;
  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();
  const { pathAfter } = useWorkflow();

  const hasError = attempts > 0;
  const error = tMd('batchPrescreen.formPrescreen.error', {
    count: maxAttempts - attempts,
  });

  return (
    <Fragment>
      <PageContainer maxWidth="sm">
        <Form>
          <Box mb={5}>
            <Title css={styles.titleText}>
              {tMd('batchPrescreen.formPrescreen.title')}{' '}
            </Title>
            <div css={styles.idPrompt}>
              {tMdMultiline('batchPrescreen.formPrescreen.idPrompt')}
            </div>
            <div css={styles.formFields}>
              <Field name="prescreenId">
                {(fieldProps: FieldProps) => (
                  <FormTextField
                    id="prescreenId"
                    fieldProps={fieldProps}
                    mask={prescreenIdMask}
                    label={t('formLabel.prescreenId')}
                    error={hasError}
                    helperText={hasError ? error : undefined}
                  />
                )}
              </Field>
            </div>
          </Box>
          <ActionButtonContainer>
            <IovationButton
              id="formPrescreenSubmit"
              type="submit"
              disabled={!isValid}
              css={styles.submitButton}
              loading={isSubmitting}
            >
              {t('batchPrescreen.formPrescreen.submit')}
            </IovationButton>
            {attempts > 0 ? (
              <Button
                component={Link}
                to={pathAfter(Step.BATCH_PRESCREEN_START)}
                id="continueToApplication"
                variant="text"
              >
                {t('batchPrescreen.formPrescreen.secondarySubmit')}
              </Button>
            ) : null}
            <CancelButton />
          </ActionButtonContainer>
        </Form>
      </PageContainer>
    </Fragment>
  );
};

export default FormPrescreenIdView;
